// Child TS - add back the reloadStabilizedVideo call in pollOnce for SUCCEEDED, but since parent handles it, optional. But to ensure, add a small delay or something. Actually, remove the pause from child, let parent handle.

import { Component, Vue, Prop, Ref, Watch } from 'vue-property-decorator';
import { Camera } from '@/models/camera';
import { Project } from '@/models/project';
import { Video } from '@/models/video';
import { Storage } from 'aws-amplify';
import { Action, State } from 'vuex-class';
// @ts-ignore
import Plyr from 'plyr/dist/plyr.js';
// @ts-ignore no d.ts
import RadialProgressBar from 'vue-radial-progress';
import { NotificationMessage, Socket } from '@/store/types';
import { Logger } from 'aws-amplify';
const logger = new Logger('TimelapsePreview');

@Component({
  components: {
    RadialProgressBar,
  },
})
export default class TimelapsePreview extends Vue {
  @Prop({ required: true, type: Object }) public readonly camera!: Camera;
  @Prop({ required: true, type: Object }) public readonly project!: Project;
  @Prop({ type: Object }) public readonly lastVideo?: Video;
  @Prop({ required: true, type: Boolean }) public readonly timelapseRending!: boolean;
  @Prop({ type: Boolean, default: false }) public readonly stabilizationEnabled!: boolean;
  @Prop({ type: Number, default: 0 }) public readonly totalPhotos!: number;

  @Action('loadTimelapse', { namespace: 'project' }) private loadTimelapse!: () => Promise<void>;

  @State('loadingTimelapse', { namespace: 'project' }) private loadingTimelapse!: boolean;
  @State('socket') private socketMessage!: Socket;

  @Ref('videoPlayer') private videoPlayer?: HTMLVideoElement;

  private videoUrl: string | null = null;
  private player: any = null; // type simplifié, suffisant pour l’usage

  get plyrOptions(): any {
    const $t = this.$t.bind(this);

    return {
      autoplay: false,
      clickToPlay: true,
      hideControls: true,
      seekTime: 10,
      resetOnEnd: true,
      disableContextMenu: false,
      loadSprite: true,
      controls: [
        'play-large',
        'restart',
        'rewind',
        'play',
        'fast-forward',
        'progress',
        'current-time',
        // 'duration',
        'mute',
        'volume',
        'captions',
        'settings',
        'pip',
        'airplay',
        //'download',
        'fullscreen',
      ],
      tooltips: {
        controls: true, // ✅ tooltips sur les boutons
        seek: true, // tooltip sur la barre de progression
      },
      i18n: {
        restart: $t('video.restart').toString(),
        rewind: $t('video.rewind').toString(),
        play: $t('video.play').toString(),
        pause: $t('video.pause').toString(),
        fastForward: $t('video.fastForward').toString(),
        seek: $t('video.seek').toString(),
        seekLabel: $t('video.seekLabel').toString(),
        played: $t('video.played').toString(),
        buffered: $t('video.buffered').toString(),
        currentTime: $t('video.currentTime').toString(),
        duration: $t('video.duration').toString(),
        volume: $t('video.volume').toString(),
        mute: $t('video.mute').toString(),
        unmute: $t('video.unmute').toString(),
        enableCaptions: $t('video.enableCaptions').toString(),
        disableCaptions: $t('video.disableCaptions').toString(),
        settings: $t('video.settings').toString(),
        speed: $t('video.speed').toString(),
        normal: $t('video.normal').toString(),
        quality: $t('video.quality').toString(),
        loop: $t('video.loop').toString(),
        enterFullscreen: $t('video.enterFullscreen').toString(),
        exitFullscreen: $t('video.exitFullscreen').toString(),
        download: $t('video.download').toString(),
        reset: $t('common.reset').toString(), // tu as "common.reset"
        disabled: $t('global.need_video').toString(), // ou autre clé
        enabled: $t('global.activate').toString(),
        all: $t('global.timelapse').toString(), // au choix
        start: $t('global.start').toString(),
        end: $t('global.end').toString(),
      },
    };
  }
  private progressValue = 0;
  private workerProgress: number[] = [];
  private intervalID: number[] = [];

  private stabilizationJobId: string | null = null;
  private stabilizationStatus: string | null = null;
  private stabilizationInterval: number | null = null;

  private isStabilizing = false;
  private showStabilizationOverlay = false;

  private softProgressTimer: number | null = null;
  private currentStepIndex = -1; // index dans STAB_STEPS

  private videoReady = false;
  private loadingVideo = false;

  private stabProgress = 0;

  private readonly PHASE2_THRESHOLD = 80;

  private get softProgressRate(): number {
    if (this.totalPhotos <= 0) return 0.01; // Fallback si pas de photos

    const secondsPerPhoto = 0.82; // Métrique observée
    const expectedTotalSeconds = this.totalPhotos * secondsPerPhoto;
    const scalingFactor = 0.8; // Couvre ~80% de la barre pendant le temps estimé
    const rate = (scalingFactor / expectedTotalSeconds) * 100; // % par seconde

    // Boost pour petits volumes (<100 photos : x2 pour fluidité)
    return this.totalPhotos < 100 ? rate * 2 : Math.max(0.005, rate); // Min 0.5%/s
  }

  private get STAB_STEPS(): Array<{ status: string; base: number }> {
    const baseSteps = [
      { status: 'SUBMITTED', base: 0 },
      { status: 'PENDING', base: 5 },
      { status: 'RUNNABLE', base: 10 },
      { status: 'STARTING', base: 15 },
      { status: 'RUNNING', base: 20 },
      { status: 'SUCCEEDED', base: 100 },
    ];

    return baseSteps;
  }

  get showVideoShell(): boolean {
    if (!this.timelapseRending) return true; // lecture d’une vidéo déjà prête
    return this.progressValue >= this.PHASE2_THRESHOLD;
  }

  get canInitPlayer(): boolean {
    return this.showVideoShell && !!(this.lastVideo && this.lastVideo.key && this.videoUrl);
  }

  get videoDomKey(): string {
    // change quand l’URL change pour forcer un remount propre du <video>
    return this.videoUrl ? this.videoUrl : 'no-src';
  }

  @Watch('canInitPlayer', { immediate: true })
  onCanInitPlayer(val: boolean, oldVal: boolean) {
    if (val) this.safeInitPlayer();
  }

  private async safeInitPlayer() {
    // on ne fait rien si pas de vidéo dispo
    if (!this.canInitPlayer || !this.videoUrl) return;

    // évite les doubles instances
    if (this.player) {
      this.player.destroy();
      this.player = null;
    }

    // Attends vraiment que le DOM soit prêt
    await this.$nextTick();
    await new Promise(requestAnimationFrame);

    const el = this.$refs.videoPlayer as HTMLVideoElement | undefined;
    if (!el) {
      // DOM pas encore prêt → on retente très vite
      setTimeout(() => this.safeInitPlayer(), 60);
      return;
    }

    // Création du player Plyr
    this.player = new Plyr(el, this.plyrOptions);

    // Source vidéo (équivalent de player.src(...) avec Video.js)
    this.player.source = {
      type: 'video',
      sources: [
        {
          src: this.videoUrl,
          type: 'video/mp4',
        },
      ],
    };

    this.loadingVideo = true;

    // Événements : on se cale sur le "ready" / "loadeddata"
    this.player.on('ready', () => {
      this.videoReady = true;
      // la vidéo est prête, mais on garde loadingVideo jusqu'à loadeddata
    });

    this.player.on('loadeddata', () => {
      this.loadingVideo = false;
      this.showStabilizationOverlay = this.isStabilizing;
    });

    this.player.on('error', () => {
      this.loadingVideo = false;
    });
  }

  private findStepIndex(status: string | null): number {
    return status ? this.STAB_STEPS.findIndex((s: { status: string; base: number }) => s.status === status) : -1; // AJOUT: this. + type pour s
  }

  // Plafond dynamique : "juste en-dessous" de la prochaine step
  private nextCeil(): number {
    const next = this.currentStepIndex + 1;
    if (next < this.STAB_STEPS.length) return this.STAB_STEPS[next].base - 0.1;
    return 99.5;
  }

  private startSoftProgressLoop() {
    if (this.softProgressTimer) return;
    this.softProgressTimer = window.setInterval(() => {
      if (!this.isStabilizing) return this.stopSoftProgressLoop();
      const ceil = this.nextCeil();
      if (this.stabProgress < ceil) {
        const remaining = ceil - this.stabProgress;
        const step = Math.max(0.05, Math.min(this.softProgressRate, remaining * 0.12));
        this.stabProgress = Math.min(ceil, this.stabProgress + step);
      }
    }, 1000);
  }

  private animateStabTo(target: number, duration = 1000, onComplete?: () => void): void {
    const start = this.stabProgress;
    const delta = target - start;
    if (delta <= 0) {
      onComplete && onComplete();
      return;
    }

    const t0 = performance.now();
    const tick = (t: number) => {
      const p = Math.min((t - t0) / duration, 1);
      const eased = p * (2 - p);
      this.stabProgress = start + delta * eased;
      if (p < 1) requestAnimationFrame(tick);
      else onComplete && onComplete();
    };
    requestAnimationFrame(tick);
  }

  private stopSoftProgressLoop() {
    if (this.softProgressTimer) {
      clearInterval(this.softProgressTimer);
      this.softProgressTimer = null;
    }
  }

  public async mounted(): Promise<void> {
    logger.info('mounted');
    //console.log('🔍 Total photos received in mounted:', this.totalPhotos);

    // Charge la vidéo existante
    if (this.lastVideo !== undefined) {
      this.onLastVideoChange(this.lastVideo, undefined);
    }

    // 🔁 Vérifie si une stabilisation était en cours
    const saved = localStorage.getItem('erige-stabilization');
    if (saved) {
      try {
        const info = JSON.parse(saved);
        if (info.cameraId === this.camera.id && info.projectId === this.project.id) {
          logger.info('♻️ Reprise de la stabilisation en cours après reload', info);
          this.stabilizationJobId = info.jobId;
          this.isStabilizing = true;
          this.showStabilizationOverlay = true;
          this.stabProgress = Math.max(0, this.stabProgress || 2);

          // Optionnel : on redémarre le polling immédiatement
          await this.startStabilizationPolling();
        } else {
          logger.info('➡️ Stabilisation enregistrée pour une autre caméra, on ignore.');
        }
      } catch (err) {
        console.error('❌ Erreur lecture stabilisation locale', err);
        localStorage.removeItem('erige-stabilization');
      }
    }
  }

  private animateToBase(target: number, duration = 2000, onComplete?: () => void): void {
    const startValue = this.progressValue;
    const delta = target - startValue;
    if (delta <= 0) {
      if (onComplete) onComplete();
      return;
    }

    const startTime = performance.now();
    const animate = (time: number) => {
      const elapsed = time - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = progress * (2 - progress);
      this.progressValue = startValue + delta * easedProgress;

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else if (onComplete) {
        onComplete();
      }
    };
    requestAnimationFrame(animate);
  }

  public getRotation(): string {
    return this.camera && this.camera.rotate ? `rotate(${this.camera.rotate}deg)` : '';
  }

  public beforeDestroy(): void {
    if (this.player) {
      this.player.destroy();
      this.player = null;
    }
    if (this.stabilizationInterval) {
      clearInterval(this.stabilizationInterval);
      this.stabilizationInterval = null;
    }
    this.stopSoftProgressLoop();
  }

  private async pollOnce(): Promise<void> {
    if (!this.stabilizationJobId) return;

    try {
      const response = await fetch(
        'https://ayg1dycdug.execute-api.eu-west-3.amazonaws.com/prod/video/stabilization/status',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ jobId: this.stabilizationJobId }),
        }
      );
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.json();
      this.stabilizationStatus = data.status || this.stabilizationStatus;
      this.showStabilizationOverlay = this.isStabilizing;

      // Met à jour l’ancre "base" de la step courante sans jamais reculer
      const idx = this.findStepIndex(this.stabilizationStatus);
      if (idx >= 0) {
        this.currentStepIndex = Math.max(this.currentStepIndex, idx);
        const base = this.STAB_STEPS[idx].base;
        if (base > this.stabProgress) this.animateStabTo(base, 1200);
      }

      switch (this.stabilizationStatus) {
        case 'SUBMITTED':
        case 'PENDING':
        case 'RUNNABLE':
        case 'STARTING':
        case 'RUNNING': {
          // 1) Si compteurs fournis par l’API
          const total = Number(data.totalPhotos ?? data.totalFrames ?? data.total ?? 0);
          const done = Number(data.processedPhotos ?? data.framesProcessed ?? data.processed ?? 0);

          if (total > 0 && done >= 0) {
            const idxRun = this.findStepIndex('RUNNING');
            const floor = idxRun >= 0 ? this.STAB_STEPS[idxRun].base : 20;
            // Mappe 0..total sur [floor .. 99.5]
            const span = 99.5 - floor;
            const mapped = floor + Math.min(span, (done / total) * span);
            if (mapped > this.stabProgress) this.animateStabTo(mapped, 600);
            // Pas de boucle douce si on a des chiffres fiables
            this.stopSoftProgressLoop();
          } else {
            // 2) Pas de compteurs → lissage auto (dépend de totalPhotos)
            this.startSoftProgressLoop();
          }
          break;
        }

        case 'SUCCEEDED': {
          this.stopSoftProgressLoop();
          this.stabProgress = 100;

          if (this.stabilizationInterval) {
            clearInterval(this.stabilizationInterval);
            this.stabilizationInterval = null;
          }

          setTimeout(async () => {
            this.isStabilizing = false;
            this.stabilizationJobId = null;
            this.showStabilizationOverlay = false;

            if (this.lastVideo?.key) {
              const fresh = (await Storage.get(this.lastVideo.key, { expires: 300 })) as string;
              this.videoUrl = fresh;
              this.loadingVideo = true;

              // 🔧 Assure la (re)création du player sur le nouveau <video>
              await this.$nextTick();
              await new Promise(requestAnimationFrame);
              this.safeInitPlayer();
            }

            // Finit le radial global après stabilisation
            this.animateToBase(100, 800, async () => {
              setTimeout(() => this.$emit('stabilization-ended'), 400);
            });
          }, 400);

          break;
        }

        case 'FAILED':
          this.stopSoftProgressLoop();
          if (this.stabilizationInterval) {
            clearInterval(this.stabilizationInterval);
            this.stabilizationInterval = null;
          }
          this.isStabilizing = false;
          this.stabilizationJobId = null;
          this.showStabilizationOverlay = false;
          console.error('❌ Stabilization failed');
          this.$emit('stabilization-ended');
          break;
      }
    } catch (err) {
      console.error('Error polling stabilization status:', err);
    }
  }

  private async startStabilizationPolling() {
    if (!this.stabilizationJobId) return;

    if (this.stabilizationInterval) {
      clearInterval(this.stabilizationInterval);
      this.stabilizationInterval = null;
    }

    // Poll immediately
    await this.pollOnce();

    // Then set interval
    this.stabilizationInterval = window.setInterval(() => {
      this.pollOnce();
    }, 3000);
  }

  private formatStatus(status: string): string {
    // clé du type "timelapse.stabilization.status.SUBMITTED"
    const key = `timelapse.stabilization.status.${status}`;
    const translated = this.$t(key);

    // si la clé n’existe pas, $t renvoie la clé elle-même → fallback
    if (translated === key) {
      return status;
    }

    return translated.toString();
  }

  @Watch('lastVideo')
  public onLastVideoChange(val: Video | undefined, oldVal: Video | undefined): void {
    logger.info('onLastVideoChange', { val, oldVal });
    if (this.player) {
      this.player.destroy();
      this.player = null;
    }
    this.loadingVideo = true;
    this.videoReady = false;
    if (this.lastVideo && this.lastVideo.key) {
      Storage.get(this.lastVideo.key)
        .then((url) => {
          this.videoUrl = url as string;
        })
        .catch((err) => {
          logger.error('Failed to fetch video URL', err);
          this.loadingVideo = false;
        });
    } else {
      this.loadingVideo = false;
    }
  }

  @Watch('videoUrl')
  onVideoUrlChanged() {
    if (this.canInitPlayer) this.safeInitPlayer();
  }

  @Watch('totalPhotos')
  private onTotalPhotosChange(newVal: number) {
    if (newVal > 0 && this.isStabilizing) {
      // Recalcule la rate et relance le loop si en cours
      this.stopSoftProgressLoop();
      this.startSoftProgressLoop();
    }
  }

  @Watch('timelapseRending')
  public save(b: boolean): void {
    logger.info('timelapseRendingChange', { b });
    if (b) {
      this.progressValue = 0;
      if (this.player) {
        this.player.destroy();
        this.player = null;
      }
    }
  }

  @Watch('$i18n.locale')
  onLocaleChanged() {
    // Recrée le player avec les nouvelles traductions
    if (this.player) {
      this.player.destroy();
      this.player = null;
    }
    if (this.canInitPlayer) {
      this.safeInitPlayer();
    }
  }

  @Watch('socketMessage.message')
  public async onSocketMessageChange(val: NotificationMessage): Promise<void> {
    if (val.timelapse !== undefined && val.projectId === this.project.id && val.cameraId === this.camera.id) {
      switch (val.timelapse) {
        case 'startWorker':
          logger.info('startWorker ', val.workerCount);
          this.progressValue = 1;
          this.workerProgress = new Array(val.workerCount as number).fill(0);
          break;

        case 'workerDownloadDone':
          logger.info('workerDownloadDone ', val.workerId);
          clearInterval(this.intervalID[val.workerId as number]);
          this.intervalID[val.workerId as number] = window.setInterval(() => {
            this.workerProgress[val.workerId as number] += 0.55;
            this.refreshGlobalProgress();
          }, 1000);
          break;

        case 'workerFFmpegDone':
          logger.info('workerFFmpegDone ', val.workerId);
          this.workerProgress[val.workerId as number] = 100;
          window.clearInterval(this.intervalID[val.workerId as number]);
          this.refreshGlobalProgress();
          break;

        case 'timelapseFinalize':
          logger.info('timelapseFinalize');
          this.animateToBase(90); // Anime vers 90 au lieu de set direct
          break;

        case 'stabilizationStarted':
          this.stabilizationJobId = (val as any).jobId as string;
          this.isStabilizing = true;
          this.showStabilizationOverlay = true;
          this.currentStepIndex = -1;
          this.stabProgress = 0; // <-- nouveau départ à 0%

          this.startSoftProgressLoop(); // en attendant le premier poll
          this.startStabilizationPolling();
          break;

        case 'ready':
          if (this.stabilizationEnabled) {
            logger.info('🧯 ready reçu mais stabilisation enabled — on ignore');
            break;
          }
          this.animateToBase(100, 1000, () => {
            // Anime vers 100, puis délai 2s avant emit
            setTimeout(() => {
              this.$emit('stabilization-ended'); // Ou 'rendering-complete' si tu ajoutes
            }, 2000);
          });
          break;
      }
    }
  }

  private refreshGlobalProgress() {
    const newValue =
      (25 * this.intervalID.filter((x: number) => x !== undefined).length) / this.workerProgress.length +
      this.workerProgress.reduce((pv, cv) => pv + cv, 0) / this.workerProgress.length / 2.0;

    if (Math.abs(newValue - this.progressValue) > 1) {
      // Anime si changement >1%
      this.animateToBase(newValue, 1000); // Animation courte pour updates fréquents
    } else {
      this.progressValue = newValue; // Set direct pour petits changements
    }
  }
}
